<script setup lang="ts">
import { ActionButton, FileUpload, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AccountPicker } from '@/features/entries'

const props = defineProps<{
  isUploading: boolean
  errorDetail: string | undefined
}>()

const emit = defineEmits<{ submit: [{ accountId: string; file: File }] }>()

const { t } = useI18n()

const accountId = ref<string | null>(null)
const file = ref<File | null>(null)

const canSubmit = computed(() => accountId.value !== null && file.value !== null && !props.isUploading)

watch(
  () => props.isUploading,
  (uploading, wasUploading) => {
    if (wasUploading && !uploading && props.errorDetail === undefined) {
      file.value = null
      accountId.value = null
    }
  },
)

function onSubmit(): void {
  if (accountId.value === null || file.value === null) return
  emit('submit', { accountId: accountId.value, file: file.value })
}
</script>

<template>
  <form
    class="flex flex-col gap-[var(--space-block)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-block)]"
    @submit.prevent="onSubmit"
  >
    <div class="flex flex-wrap items-end gap-[var(--space-block)]">
      <FileUpload
        v-model="file"
        class="w-72"
        :label="t('receiptScan.receiptImage')"
        accept="image/jpeg,image/png,image/webp,image/gif"
        :accept-hint="t('receiptScan.acceptHint')"
        :browse-label="t('receiptScan.browseFile')"
        :drop-hint="t('receiptScan.dropHint')"
        :remove-label="t('receiptScan.removeFile')"
        :invalid-type-message="t('receiptScan.invalidFileType')"
      />

      <label class="flex w-64 flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('receiptScan.paymentSource')
        }}</span>
        <AccountPicker
          id="receipt-payment-account"
          :label="t('receiptScan.paymentSource')"
          :model-value="accountId"
          :filter-class="['ASSET', 'LIABILITY']"
          @update:model-value="(value) => (accountId = value)"
        />
      </label>

      <ActionButton
        size="lg"
        type="submit"
        :disabled="!canSubmit"
        class="text-[length:var(--text-step-3)] font-medium"
      >
        {{ isUploading ? t('receiptScan.uploading') : t('receiptScan.scanReceipt') }}
      </ActionButton>
    </div>

    <StatusTag v-if="errorDetail !== undefined" tone="danger" role="alert">
      {{ errorDetail }}
    </StatusTag>
  </form>
</template>
